import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { withTranslation } from "react-i18next";
import SubNav from "../../shared/subnav/SubNav";
import BrandSettings from "./brandSettings/BrandSettings";
import styles from "./AccessoriesWrapper.module.scss";
import { BRANDS } from "./Brands";
import ConfirmDialog from "../../shared/confirmDialog/ConfirmDialog";
import { IS_FORD_BRAND } from "../../utils/EmpUtil";
import NamePlateSettings from "./namePlateSettings/NamePlateSettings";
import HasPermissionTo from "../../common/HasPermissionTo";
import AccessoriesSearch from "./search/AccessoriesSearch";
import CategorySettings from "./categorySettings/CategorySettings";

const getSelectedTab = (history) =>
  history?.location?.state?.redirectToAccSearch ? 3 : 0;

const AccessoriesWrapper = (props) => {
  const { user, t, history, changeBrand } = props;
  const [selectedTab, setSelectedTab] = useState(() => getSelectedTab(history));
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [enableTabs, setEnableTabs] = useState(selectedTab > 0);
  const [showWarning, setShowWarning] = useState(false);
  const [selectedDisabledTab, setSelectedDisabledTab] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const brandCodeRef = useRef(null);
  const newTabChangeRef = useRef(null);
  const isFordBrand = IS_FORD_BRAND(user.brand);

  useEffect(() => {
    setIsDataLoaded(true);
  }, []);

  const confirmDialogProps = useMemo(
    () => ({
      title: t("BrandSettings.dialog.confirm.warningTitle"),
      bodyText: t("BrandSettings.dialog.confirm.warning"),
      confirm: isFordBrand
        ? t("BrandSettings.dialog.confirm.F.continue")
        : t("BrandSettings.dialog.confirm.L.confirm"),
      cancel: isFordBrand
        ? t("BrandSettings.dialog.confirm.F.cancel")
        : t("BrandSettings.dialog.confirm.L.cancel"),
    }),
    [isFordBrand, t]
  );

  const changeTabTo = useCallback(
    (nextTab) => {
      if (enableTabs) {
        newTabChangeRef.current = null;
        setSelectedTab(nextTab);
        setConfirmDialog(false);
        return;
      }
      setShowWarning(nextTab === 1 || nextTab === 2 || nextTab === 3);
      setSelectedDisabledTab(nextTab);
    },
    [enableTabs]
  );

  const handleTabChange = useCallback(
    (_, nextTab) => {
      if (user.displayWarning) {
        newTabChangeRef.current = nextTab;
        setConfirmDialog(true);
        return;
      }
      changeTabTo(nextTab);
    },
    [changeTabTo, user.displayWarning]
  );

  const enableOtherTabs = useCallback((value) => {
    setEnableTabs(value);
    setShowWarning(false);
  }, []);

  const changeBrandSettings = useCallback(() => {
    changeBrand(brandCodeRef.current);
    document.body.style.backgroundColor = IS_FORD_BRAND(brandCodeRef.current)
      ? "#ffffff"
      : "#f6f7f7";
  }, [changeBrand]);

  const handleClose = useCallback(() => {
    newTabChangeRef.current = null;
    setConfirmDialog(false);
  }, []);

  const handleWarningDismiss = useCallback(() => {
    setShowWarning(false);
  }, []);

  const handleConfirm = useCallback(
    (event) => {
      if (event) {
        event.preventDefault();
      }
      user.showWarning(false);
      if (newTabChangeRef.current === null) {
        changeBrandSettings();
      } else {
        changeTabTo(newTabChangeRef.current);
      }
    },
    [changeBrandSettings, changeTabTo, user]
  );

  const handleBrandChange = useCallback(
    (event) => {
      if (event) {
        event.preventDefault();
      }
      brandCodeRef.current = event.target.value;
      if (user.displayWarning) {
        setConfirmDialog(true);
        return;
      }
      changeBrandSettings();
    },
    [changeBrandSettings, user.displayWarning]
  );

  const tabs = useMemo(
    () => [
      {
        menuName: t("ProgressBar.brandSettings"),
        enabled: true,
        id: "brandSettings",
        hide: !user.permissions.rolePermissions.hasOwnProperty("brandSettingsTabUS"),
      },
      {
        menuName: t("ProgressBar.namePlateSettings"),
        enabled: true,
        disabledStyle: !enableTabs,
        id: "namePlateSettings",
        hide: !user.permissions.rolePermissions.hasOwnProperty("namePlateSettingsTabUS"),
      },
      {
        menuName: t("ProgressBar.categorySettings"),
        enabled: true,
        disabledStyle: !enableTabs,
        id: "categorySettings",
        hide: !user.permissions.rolePermissions.hasOwnProperty("categorySettingsTabUS"),
      },
      {
        menuName: t("ProgressBar.search"),
        enabled: true,
        disabledStyle: !enableTabs,
        id: "accessoriesSearch",
        hide: !user.permissions.rolePermissions.hasOwnProperty("accessoriesSearchTabUS"),
      },
    ],
    [enableTabs, t, user.permissions.rolePermissions]
  );

  if (isDataLoaded) {
    return (
      <div className="pageWrapper">
        <SubNav
          selectedTab={selectedTab}
          restrictDropdown
          showCustomDropdown
          title={t("GlobalNavBar.accessoriesPricing")}
          action={handleTabChange}
          value={user.brand}
          onChange={handleBrandChange}
          dropDownValues={BRANDS}
          tabs={tabs}
          {...props}
        />
        {showWarning && (
          <div className={styles.dialogTitle}>
            <span className={styles.warning} />
            {selectedDisabledTab === 2 ? (
              <div>{t("CategorySettings.warningMessage")}</div>
            ) : (
              <div>{t("BrandSettings.warningMessage")}</div>
            )}
            <button
              type="button"
              className={styles.delete}
              aria-label="Dismiss warning"
              onClick={handleWarningDismiss}
            />
          </div>
        )}
        <div className="pageWrapper">
          <HasPermissionTo
            perform={["brandSettingsTabUS"]}
            permissions={user.permissions.rolePermissions}
            condition={selectedTab === 0}
            render={() => (
              <BrandSettings
                {...props}
                handleTabChange={handleTabChange}
                enableOtherTabs={enableOtherTabs}
              />
            )}
          />
          <HasPermissionTo
            perform={["namePlateSettingsTabUS"]}
            permissions={user.permissions.rolePermissions}
            condition={selectedTab === 1}
            render={() => (
              <NamePlateSettings {...props} handleTabChange={handleTabChange} />
            )}
          />
          <HasPermissionTo
            perform={["categorySettingsTabUS"]}
            permissions={user.permissions.rolePermissions}
            condition={selectedTab === 2}
            render={() => (
              <CategorySettings
                {...props}
                confirmDialogProps={confirmDialogProps}
                handleTabChange={handleTabChange}
              />
            )}
          />
          <HasPermissionTo
            perform={["accessoriesSearchTabUS"]}
            permissions={user.permissions.rolePermissions}
            condition={selectedTab === 3}
            render={() => (
              <AccessoriesSearch {...props} handleTabChange={handleTabChange} />
            )}
          />
        </div>

        <ConfirmDialog
          open={confirmDialog}
          dialogProps={confirmDialogProps}
          onConfirm={handleConfirm}
          onCancel={handleClose}
          boldTitle
          showWarningIcon
          onClose={handleClose}
          {...props}
        />
      </div>
    );
  }

  return null;
};

AccessoriesWrapper.propTypes = {
  changeBrand: PropTypes.func.isRequired,
  history: PropTypes.shape({
    location: PropTypes.shape({
      state: PropTypes.shape({
        redirectToAccSearch: PropTypes.bool,
      }),
    }),
  }),
  t: PropTypes.func.isRequired,
  user: PropTypes.shape({
    brand: PropTypes.string.isRequired,
    displayWarning: PropTypes.bool,
    permissions: PropTypes.shape({
      rolePermissions: PropTypes.object.isRequired,
    }).isRequired,
    showWarning: PropTypes.func.isRequired,
  }).isRequired,
};

export default withTranslation("emp")(AccessoriesWrapper);
